import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import AdvancedSection from '../components/AdvancedSection';
import AdvRadioGroup from '../components/AdvRadioGroup';
import SmallToggleRow from '../components/SmallToggleRow';

const TONES = [
    { value: 'warm_professional', label: 'Warm & Professional', desc: 'Friendly but polished. Uses first names. Most popular.' },
    { value: 'concise', label: 'Clear & Concise', desc: 'Short, direct. No filler phrases. Gets to the point.' },
    { value: 'formal', label: 'Formal', desc: 'Traditional, professional tone. Suits specialist practices.' },
];

export default function ToneStep({ formData, onChange }) {
    const currentTone = formData.ai_tone ?? 'warm_professional';

    return (
        <Box>
            <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#00A76F', mb: '10px',
            }}>
                WhatsApp AI personality
            </Typography>
            <Typography sx={{
                fontSize: 22, fontWeight: 800, color: '#0D0D0D',
                lineHeight: 1.3, letterSpacing: '-0.4px', mb: 1,
            }}>
                How should WhatsApp AI sound to patients?
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: '#7A7A7A', lineHeight: 1.6, mb: '28px' }}>
                This applies to all automated messages. You can always fine-tune individual messages on each step.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: '20px' }}>
                {TONES.map((tone) => {
                    const selected = currentTone === tone.value;
                    return (
                        <Box
                            key={tone.value}
                            onClick={() => onChange('ai_tone', tone.value)}
                            sx={{
                                p: '15px 16px',
                                borderRadius: '16px',
                                border: '1.5px solid',
                                borderColor: selected ? '#00A76F' : '#EBEBEB',
                                backgroundColor: selected ? '#F0FAF6' : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                userSelect: 'none',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                '&:hover': { borderColor: selected ? '#00A76F' : '#C0E8D8' },
                            }}
                        >
                            <Box sx={{
                                flexShrink: 0, width: 18, height: 18, borderRadius: '50%',
                                border: '2px solid', mt: '2px',
                                borderColor: selected ? '#00A76F' : '#EBEBEB',
                                backgroundColor: selected ? '#00A76F' : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                            }}>
                                {selected && (
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fff' }} />
                                )}
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0D0D0D' }}>
                                    {tone.label}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: '#7A7A7A', mt: '2px', lineHeight: 1.4 }}>
                                    {tone.desc}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <AdvancedSection>
                <AdvRadioGroup
                    label="Sign-off name"
                    value={formData.sign_off_name ?? 'practice'}
                    onChange={(val) => onChange('sign_off_name', val)}
                    options={[
                        { value: 'practice', label: 'Practice name', desc: '"The Academy Dental Team"' },
                        { value: 'team', label: 'Generic team', desc: '"The Team"' },
                        { value: 'none', label: 'No sign-off' },
                    ]}
                />
                <SmallToggleRow
                    checked={formData.use_emoji ?? true}
                    onChange={(val) => onChange('use_emoji', val)}
                    label="Include emojis in WhatsApp AI replies"
                    desc="Adds 😊 ✅ to messages. Works well with Warm & Professional."
                />
            </AdvancedSection>
        </Box>
    );
}

ToneStep.propTypes = {
    formData: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
};
